/**
 * FakeMapAdapter — hermetic MapProvider implementation used by ALL tests
 * (ODE-P07-T02, REQ-084). It performs no network calls, holds no external
 * dependency, and mirrors the adapter contract: mount → setMarkers →
 * destroy, tracking its own lifecycle so tests can assert on it.
 *
 * Positions render EXACTLY as given (last known fix; never extrapolated).
 * This adapter also serves the `VITE_MAP_PROVIDER=fake` dev/demo mode.
 *
 * Traces to: ODE-PRD-REQ-084, ODE-PRD-REQ-090.
 */

import type { MapMarkerView, MapProvider } from './map-provider.js';

/** Observable lifecycle snapshot of the fake adapter (test assertions). */
export interface FakeMapState {
  readonly mounted: boolean;
  readonly containers: readonly HTMLElement[];
  readonly markers: readonly MapMarkerView[];
  readonly destroyed: boolean;
  readonly setMarkersCalls: number;
}

export class FakeMapAdapter implements MapProvider {
  readonly name = 'fake';
  private container: HTMLElement | null = null;
  private currentMarkers: readonly MapMarkerView[] = [];
  private destroyedFlag = false;
  private mounts = 0;
  private markerCalls = 0;

  mount(container: HTMLElement): void {
    if (this.destroyedFlag) {
      throw new Error('FakeMapAdapter cannot be remounted after destroy');
    }
    this.container = container;
    this.mounts += 1;
  }

  setMarkers(markers: readonly MapMarkerView[]): void {
    if (this.container === null || this.destroyedFlag) return;
    // Copy defensively; positions are rendered exactly as given.
    this.currentMarkers = markers.map((marker) => ({ ...marker }));
    this.markerCalls += 1;
  }

  destroy(): void {
    this.container = null;
    this.currentMarkers = [];
    this.destroyedFlag = true;
  }

  /** Inspection surface for tests (never used by production code paths). */
  inspect(): FakeMapState {
    return {
      mounted: this.container !== null,
      containers: this.container === null ? [] : [this.container],
      markers: this.currentMarkers,
      destroyed: this.destroyedFlag,
      setMarkersCalls: this.markerCalls,
    };
  }
}
